import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  Search,
  X,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Clock,
  TrendingUp,
  Sparkles,
  Check,
  Pencil,
  Image,
  Film,
  FileUp,
  ExternalLink,
  Phone,
  Copy,
  Reply,
  AlertCircle,
  Plus,
  Trash2,
  RefreshCw,
  Settings,
  Hash,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogFooter } from '@/components/ui/dialog';
import { useTemplates } from '@/hooks/use-templates';
import { useSavedTemplates } from '@/hooks/use-saved-templates';
import { useMessageStore } from '@/stores/message-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { getStorageItem, setStorageItem } from '@/lib/utils/storage';
import { TemplateEditorDialog } from './template-builder';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { useUIStore } from '@/stores/ui-store';
import type { MessageTemplate } from '@/types/messaging';

// --- Template usage tracking ---

interface TemplateUsageEntry {
  usedCount: number;
  timestamp: number;
}

type TemplateUsageMap = Record<string, TemplateUsageEntry>;

function getTemplateUsage(): TemplateUsageMap {
  return getStorageItem<TemplateUsageMap>('template_usage', {});
}

function recordTemplateUsage(templateId: string): void {
  const usage = getTemplateUsage();
  const existing = usage[templateId];
  usage[templateId] = {
    usedCount: (existing?.usedCount ?? 0) + 1,
    timestamp: Date.now(),
  };
  setStorageItem('template_usage', usage);
}

type TemplateLabel = 'last_used' | 'frequently_used' | 'new' | null;

function getTemplateLabels(templates: MessageTemplate[]): Map<string, TemplateLabel> {
  const usage = getTemplateUsage();
  const labels = new Map<string, TemplateLabel>();
  const knownIds = new Set(Object.keys(usage));

  let lastUsedId: string | null = null;
  let lastUsedTs = 0;
  for (const [id, entry] of Object.entries(usage)) {
    if (entry.timestamp > lastUsedTs) {
      lastUsedTs = entry.timestamp;
      lastUsedId = id;
    }
  }

  const frequentCandidates = Object.entries(usage)
    .filter(([id, entry]) => entry.usedCount >= 3 && id !== lastUsedId)
    .sort((a, b) => b[1].usedCount - a[1].usedCount || b[1].timestamp - a[1].timestamp)
    .slice(0, 3)
    .map(([id]) => id);

  const frequentSet = new Set(frequentCandidates);

  for (const t of templates) {
    if (t.id === lastUsedId) {
      labels.set(t.id, 'last_used');
    } else if (frequentSet.has(t.id)) {
      labels.set(t.id, 'frequently_used');
    } else if (!knownIds.has(t.id)) {
      labels.set(t.id, 'new');
    } else {
      labels.set(t.id, null);
    }
  }

  return labels;
}

function sortTemplates(
  templates: MessageTemplate[],
  labels: Map<string, TemplateLabel>,
): MessageTemplate[] {
  const priority: Record<string, number> = {
    last_used: 0,
    frequently_used: 1,
    new: 2,
  };
  return [...templates].sort((a, b) => {
    const la = labels.get(a.id);
    const lb = labels.get(b.id);
    const pa = la ? priority[la] : 3;
    const pb = lb ? priority[lb] : 3;
    if (pa !== pb) return pa - pb;
    return (a.name || '').localeCompare(b.name || '');
  });
}

// --- Components ---

interface TemplatePickerProps {
  /** When true, opens the template dialog from external trigger (e.g. / shortcut) */
  externalOpen?: boolean;
  /** Called when externally-triggered dialog is closed */
  onExternalOpenChange?: (open: boolean) => void;
  /**
   * When true, the selected template cannot be changed or cleared — only
   * placeholder variables can be edited. Used in webhook template edit mode.
   */
  lockTemplate?: boolean;
  /** Workflow module fields for # autocomplete inside variable inputs */
  workflowFields?: Array<{ value: string; label: string }>;
}

export function TemplatePicker({ externalOpen, onExternalOpenChange, lockTemplate = false, workflowFields }: TemplatePickerProps = {}) {
  const channel = useMessageStore((s) => s.channel);
  const selectedTemplate = useMessageStore((s) => s.selectedTemplate);
  const setTemplate = useMessageStore((s) => s.setTemplate);
  const setBody = useMessageStore((s) => s.setBody);
  const templateVariables = useMessageStore((s) => s.templateVariables);
  const setTemplateVariable = useMessageStore((s) => s.setTemplateVariable);
  const recipients = useMessageStore((s) => s.recipients);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);

  const { templates: tpaTemplates, loading: tpaLoading, forceRefresh: forceRefreshTemplates } = useTemplates(channel);
  const { templates: savedTemplates, loading: savedLoading, saveTemplate, deleteTemplate } = useSavedTemplates();

  // Merge: saved (custom) templates + TPA provider templates
  const templates = useMemo(() => {
    const all = [...savedTemplates];
    all.push(...tpaTemplates);
    return all;
  }, [tpaTemplates, savedTemplates]);
  const loading = tpaLoading || savedLoading;

  const [dialogOpen, setDialogOpen] = useState(false);

  // Sync external open trigger
  useEffect(() => {
    if (externalOpen) {
      setDialogOpen(true);
    }
  }, [externalOpen]);

  // Notify parent when dialog closes
  const handleDialogChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open && onExternalOpenChange) {
      onExternalOpenChange(false);
    }
  }, [onExternalOpenChange]);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<MessageTemplate | null>(null);

  const handleSelect = useCallback((template: MessageTemplate) => {
    recordTemplateUsage(template.id);
    setDialogOpen(false);

    // Saved/custom templates (id prefix `ua_msg_template_item_`) behave like
    // plain text — they don't carry an external substitution mechanism
    // (no Twilio ContentVariables, no Meta named-placeholders), so we
    // resolve every recognized placeholder inline and drop them straight
    // into the editable body. This mirrors chat-input.tsx's
    // handleSelectTemplate so behavior is identical between the chat view
    // and the compose form view.
    //
    // Critical: `setTemplate(null)` clears body to '' in the store, so
    // it MUST be called BEFORE `setBody(resolvedBody)` — otherwise the
    // resolved text gets wiped. The order also matters because
    // `setTemplate(template)` (non-null) overwrites body with
    // `template.body` (raw, unresolved), so we skip that call entirely
    // for saved templates and stay in plain-text mode.
    const isSavedTemplate = template.id.startsWith(CUSTOM_TEMPLATE_PREFIX);
    if (isSavedTemplate) {
      let resolvedBody = template.body || '';

      // CRM recipient lookup: a single CRM record may have multiple
      // phone fields → multiple recipients sharing entityId/properties.
      // Use the first CRM recipient as the property source.
      const crmRecipients = recipients.filter((r) => r.source === 'crm');
      const uniqueEntityIds = new Set(crmRecipients.map((r) => r.entityId).filter(Boolean));
      const singleCrmRecipient = (crmRecordContext && uniqueEntityIds.size === 1)
        ? crmRecipients[0]
        : null;

      // Build a unified property-value map for the recipient with
      // case-insensitive lookup. CRMs vary on field-name casing
      // (Zoho: `Email`, `First_Name`; HubSpot: `email`, `firstname`),
      // and `base-saas-module` overlays top-level recipient fields as
      // lowercase aliases (`name`/`email`/`phone`). Without
      // case-insensitive lookup, `${Contacts.Email}` misses the
      // lowercase `properties.email` set from `recipient.email` when
      // Zoho's own `Email` field came back blank, leaving the literal
      // placeholder in the message body.
      const buildRecipientLookup = () => {
        const map: Record<string, string> = {};
        if (singleCrmRecipient) {
          const props = singleCrmRecipient.properties || {};
          for (const [k, v] of Object.entries(props)) {
            if (v != null && v !== '') map[k.toLowerCase()] = String(v);
          }
          // Standard top-level aliases (override-on-empty so a populated
          // top-level email beats a blank properties.Email)
          if (singleCrmRecipient.name) {
            if (!map['name']) map['name'] = singleCrmRecipient.name;
            if (!map['full_name']) map['full_name'] = singleCrmRecipient.name;
            if (!map['fullname']) map['fullname'] = singleCrmRecipient.name;
          }
          if (singleCrmRecipient.phone && !map['phone']) {
            map['phone'] = singleCrmRecipient.phone;
          }
          if (singleCrmRecipient.email && !map['email']) {
            map['email'] = singleCrmRecipient.email;
          }
        }
        return map;
      };
      const recipientLookup = buildRecipientLookup();
      // For saved templates the resolved body is sent verbatim — there's
      // no variable-filling dialog or per-recipient substitution
      // downstream. So an unresolved placeholder would leak to the
      // recipient as literal `${Contacts.Email}` text. We substitute
      // empty string for missing/empty fields so the message reads
      // cleanly; the user can still see and edit the body before
      // sending.
      const lookupField = (field: string): string => {
        const v = recipientLookup[String(field).toLowerCase()];
        return v != null ? String(v) : '';
      };

      // Resolve ${CURRENT_USER.*} from the signed-in user (empty if unset)
      const user = useAuthStore.getState().user;
      if (user && /\$\{CURRENT_USER\.\w+\}/.test(resolvedBody)) {
        const userFields: Record<string, string> = {
          name: user.displayName || '',
          full_name: user.displayName || '',
          fullname: user.displayName || '',
          email: user.email || '',
          phone: user.phoneNumber || '',
        };
        resolvedBody = resolvedBody.replace(
          /(\$\{CURRENT_USER\.([A-Za-z0-9_]+)\})/g,
          (_match, _full, field) => userFields[String(field).toLowerCase()] || ''
        );
      }

      // Resolve ${Module.FieldName} from the CRM recipient's properties (empty if missing)
      if (singleCrmRecipient && /\$\{[A-Za-z0-9_]+\.\w+\}/.test(resolvedBody)) {
        resolvedBody = resolvedBody.replace(
          /(\$\{[A-Za-z0-9_]+\.([A-Za-z0-9_]+)\})/g,
          (_match, _full, field) => lookupField(field)
        );
      }

      // Resolve {{field}} from the CRM recipient's properties (empty if missing)
      if (singleCrmRecipient && /\{\{[\w-]+\}\}/.test(resolvedBody)) {
        resolvedBody = resolvedBody.replace(/\{\{([\w-]+)\}\}/g, (_match, key) =>
          lookupField(String(key).trim())
        );
      }

      // Clear any previously selected template, then write the resolved
      // body. setTemplate(null) wipes body in the store, so order is
      // load-bearing here.
      setTemplate(null);
      setBody(resolvedBody);
      return;
    }

    // TPA content template (Twilio/Meta etc.) — keep template selected
    // so the send path uses ContentVariables/named placeholders. The
    // VariableFillingDialog handles per-variable values.
    setTemplate(template);

    // Open variable/media filling dialog if template has variables OR requires media upload
    const hasVars = template.variables && template.variables.length > 0;
    const needsMedia = !!template.headerMedia;
    if (hasVars || needsMedia) {
      setPendingTemplate(template);
      setVariableDialogOpen(true);
    }
  }, [setTemplate, setBody, recipients, crmRecordContext]);

  const handleClear = () => {
    setTemplate(null);
  };

  const handleEditVariables = () => {
    if (selectedTemplate) {
      setPendingTemplate(selectedTemplate);
      setVariableDialogOpen(true);
    }
  };

  return (
    <div className="space-y-2">
      {selectedTemplate ? (
        <SelectedTemplatePreview
          template={selectedTemplate}
          templateVariables={templateVariables}
          onClear={lockTemplate ? undefined : handleClear}
          onChange={lockTemplate ? undefined : () => setDialogOpen(true)}
          onEditVariables={
            (selectedTemplate.variables && selectedTemplate.variables.length > 0) ||
            !!selectedTemplate.headerMedia
              ? handleEditVariables
              : undefined
          }
        />
      ) : lockTemplate ? null : (
        <Button
          data-testid="template-btn"
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          disabled={loading}
          className="h-8 text-xs"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileText className="h-3.5 w-3.5" />
          )}
          Use Template
          {templates.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">
              {templates.length}
            </Badge>
          )}
        </Button>
      )}

      <TemplateDialog
        open={dialogOpen}
        onOpenChange={handleDialogChange}
        templates={templates}
        loading={loading}
        selected={selectedTemplate}
        onSelect={handleSelect}
        channel={channel}
        onSaveTemplate={saveTemplate}
        onDeleteTemplate={deleteTemplate}
        forceRefresh={forceRefreshTemplates}
        refreshing={tpaLoading}
      />

      {pendingTemplate && (
        <VariableFillingDialog
          open={variableDialogOpen}
          onOpenChange={(open) => {
            setVariableDialogOpen(open);
            // If dialog is closed without completing, clear the template entirely —
            // but NOT when the template is locked (edit mode); users should still
            // be able to close and re-open without losing their body/variables.
            if (!open && !lockTemplate) {
              const vars = pendingTemplate.variables || [];
              const varsOk = vars.length === 0 || vars.every((v) => templateVariables[v]?.trim());
              const needsMedia = !!pendingTemplate.headerMedia;
              const mediaOk = !needsMedia || !!useMessageStore.getState().attachedFile;
              if (!varsOk || !mediaOk) {
                setTemplate(null);
              }
            }
          }}
          template={pendingTemplate}
          templateVariables={templateVariables}
          setTemplateVariable={setTemplateVariable}
          workflowFields={workflowFields}
          lockTemplate={lockTemplate}
        />
      )}
    </div>
  );
}

// --- Selected Template Preview ---
function SelectedTemplatePreview({
  template,
  templateVariables,
  onClear,
  onChange,
  onEditVariables,
}: {
  template: MessageTemplate;
  templateVariables: Record<string, string>;
  onClear?: () => void;
  onChange?: () => void;
  onEditVariables?: () => void;
}) {
  const hasVariables = template.variables && template.variables.length > 0;
  const filledCount = hasVariables
    ? template.variables!.filter((v) => templateVariables[v]?.trim()).length
    : 0;
  const allFilled = hasVariables && filledCount === template.variables!.length;

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-primary truncate">
              {template.name}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {template.body}
            </p>
            {hasVariables && (
              <div className="flex items-center gap-2 mt-2">
                <span className={cn('text-[10px] flex items-center gap-1', allFilled ? 'text-green-600' : 'text-muted-foreground')}>
                  {allFilled && <Check className="h-2.5 w-2.5" />}
                  {filledCount}/{template.variables!.length} variables filled
                </span>
                {onEditVariables && (
                  <button
                    onClick={onEditVariables}
                    className="text-[10px] text-primary hover:text-primary/80 flex items-center gap-0.5 transition-colors"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onChange && (
            <button
              onClick={onChange}
              className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Change template"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          {onClear && (
            <button
              onClick={onClear}
              className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              title="Remove template"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Label Badge ---
function TemplateLabelBadge({ label }: { label: TemplateLabel }) {
  if (!label) return null;

  const config = {
    last_used: {
      text: 'Last Used',
      icon: Clock,
      className: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
    },
    frequently_used: {
      text: 'Frequent',
      icon: TrendingUp,
      className: 'bg-green-500/10 text-green-600 border-green-500/30',
    },
    new: {
      text: 'New',
      icon: Sparkles,
      className: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    },
  }[label];

  const Icon = config.icon;

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium', config.className)}>
      <Icon className="h-3 w-3" />
      {config.text}
    </span>
  );
}

// --- Header media placeholder (compact for card, larger for dialog) ---
function HeaderMediaPlaceholder({
  media,
  size = 'sm',
}: {
  media: MessageTemplate['headerMedia'];
  size?: 'sm' | 'lg';
}) {
  if (!media) return null;

  const isLarge = size === 'lg';

  // ── Has preview URL → show actual media content ──
  if (media.url) {
    if (media.type === 'image') {
      return (
        <div className={cn('rounded-lg overflow-hidden bg-black/5', isLarge ? 'mb-2' : 'mb-1.5')}>
          <img src={media.url} alt="" className={cn('w-full object-cover', isLarge ? 'max-h-40' : 'max-h-24')} />
        </div>
      );
    }
    if (media.type === 'video') {
      return (
        <div className={cn('rounded-lg overflow-hidden bg-black/5 relative', isLarge ? 'mb-2' : 'mb-1.5')}>
          <video src={media.url} className={cn('w-full object-cover', isLarge ? 'max-h-40' : 'max-h-24')} muted playsInline preload="metadata" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="rounded-full bg-white/90 p-1.5">
              <Film className="h-4 w-4 text-[#075e54]" />
            </div>
          </div>
        </div>
      );
    }
    // Document with URL — show as download-style hint
    if (media.type === 'document') {
      return (
        <div className={cn(
          'rounded-md flex items-center gap-2 px-2.5',
          'bg-[#b8d4b0] dark:bg-[#1a4a3a] border border-[#8aba7e] dark:border-[#25d366]/30',
          isLarge ? 'py-3 mb-2' : 'py-1.5 mb-1.5'
        )}>
          <FileUp className={cn('text-[#3a7d34] dark:text-[#25d366] shrink-0', isLarge ? 'h-5 w-5' : 'h-3.5 w-3.5')} />
          <span className={cn('text-[#3a7d34] dark:text-[#25d366] font-medium', isLarge ? 'text-sm' : 'text-[11px]')}>
            Document
          </span>
        </div>
      );
    }
  }

  // ── No preview URL → show hint bar (same style for image, video, document) ──
  // Normalize type to handle any casing
  const normalizedType = (media.type || '').toLowerCase() as 'image' | 'video' | 'document';
  const iconMap: Record<string, typeof Image> = { image: Image, video: Film, document: FileUp };
  const labelMap: Record<string, string> = { image: 'Image', video: 'Video', document: 'Document' };
  const Icon = iconMap[normalizedType] || FileUp;
  const label = labelMap[normalizedType] || 'Media';

  return (
    <div className={cn(
      'rounded-md flex items-center gap-2 px-2.5',
      'bg-[#b8d4b0] dark:bg-[#1a4a3a] border border-[#8aba7e] dark:border-[#25d366]/30',
      isLarge ? 'py-3 mb-2' : 'py-1.5 mb-1.5'
    )}>
      <Icon className={cn('text-[#3a7d34] dark:text-[#25d366] shrink-0', isLarge ? 'h-5 w-5' : 'h-3.5 w-3.5')} />
      <span className={cn('text-[#3a7d34] dark:text-[#25d366] font-medium', isLarge ? 'text-sm' : 'text-[11px]')}>
        {label}
      </span>
    </div>
  );
}

// --- Button icon helper ---
function ButtonIcon({ type }: { type?: string }) {
  switch (type) {
    case 'url':
    case 'URL':
      return <ExternalLink className="h-3 w-3" />;
    case 'phone':
    case 'PHONE_NUMBER':
      return <Phone className="h-3 w-3" />;
    case 'copy_code':
    case 'COPY_CODE':
      return <Copy className="h-3 w-3" />;
    case 'quick_reply':
    case 'QUICK_REPLY':
      return <Reply className="h-3 w-3" />;
    default:
      return null;
  }
}

// --- Mobile phone frame with chat bubble ---
function PhonePreview({
  template,
  name,
  label,
  isSelected,
  isCustom,
  onClick,
}: {
  template: MessageTemplate;
  name: string;
  label: TemplateLabel;
  isSelected: boolean;
  isCustom?: boolean;
  onClick: () => void;
}) {
  const { body, buttons, headerMedia, footerText } = template;

  // Media type badge info
  const mediaIcon = headerMedia ? (
    headerMedia.type === 'image' ? Image :
    headerMedia.type === 'video' ? Film :
    FileUp
  ) : null;
  const mediaLabel = headerMedia ? (
    headerMedia.type === 'image' ? 'Image' :
    headerMedia.type === 'video' ? 'Video' :
    'Document'
  ) : null;

  // Parse {{var}} placeholders into parts
  const parts: (string | { variable: string })[] = [];
  const varPattern = /\{\{(\w+)\}\}/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  varPattern.lastIndex = 0;
  while ((match = varPattern.exec(body)) !== null) {
    if (match.index > lastIndex) {
      parts.push(body.slice(lastIndex, match.index));
    }
    parts.push({ variable: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex));
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'group flex flex-col rounded-2xl overflow-hidden text-left transition-all duration-200',
        'shadow-[0_2px_6px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]',
        'hover:-translate-y-0.5',
        'w-[320px] max-w-full',
        isSelected
          ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
          : 'ring-1 ring-black/[0.08] dark:ring-white/[0.12] dark:border dark:border-border/50'
      )}
    >
      {/* Header: template name + label */}
      <div className={cn(
        'px-4 py-2.5 border-b border-border/30',
        isSelected
          ? 'bg-primary/10'
          : 'bg-muted/60 dark:bg-white/10 group-hover:bg-muted dark:group-hover:bg-white/[0.14]'
      )}>
        <div className="flex items-center gap-2">
          {isSelected && (
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          )}
          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-semibold text-foreground truncate">{name}</span>
          {isCustom && (
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium bg-violet-500/10 text-violet-600 border-violet-500/30 shrink-0">
              Custom
            </span>
          )}
          {!isCustom && template.hint && (
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-400 shrink-0">
              {template.hint}
            </span>
          )}
          {headerMedia && (() => {
            const MIcon = mediaIcon!;
            return (
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-600 border-blue-500/30 dark:bg-blue-500/20 dark:text-blue-400 shrink-0">
                <MIcon className="h-2.5 w-2.5" />
                {mediaLabel}
              </span>
            );
          })()}
          <TemplateLabelBadge label={label} />
        </div>
      </div>

      {/* Chat area with wallpaper */}
      <div
        className="flex-1 px-4 py-3 min-h-[120px] flex flex-col justify-end bg-[#efeae2] dark:bg-[#0b141a]"
      >
        <div className="relative ml-1.5">
          <div className="rounded-lg rounded-tl-none bg-[#d9fdd3] dark:bg-[#005c4b] px-3 py-2.5 text-[13px] leading-relaxed text-[#111b21] dark:text-[#e9edef] shadow-[0_1px_2px_rgba(0,0,0,0.08)] max-w-full">
            {/* Header media */}
            <HeaderMediaPlaceholder media={headerMedia} size="sm" />

            {/* Body */}
            <span className="line-clamp-5">
              {parts.map((part, i) =>
                typeof part === 'string' ? (
                  <span key={i} className="whitespace-pre-wrap">{part}</span>
                ) : (
                  <span
                    key={i}
                    className="inline-block rounded bg-[#c6ebb4] dark:bg-[#025144] px-1.5 py-0.5 text-xs font-medium text-[#075e54] dark:text-[#25d366] mx-0.5"
                  >
                    {`{{${part.variable}}}`}
                  </span>
                )
              )}
            </span>

            {/* Footer */}
            {footerText && (
              <p className="text-[11px] text-[#8696a0] mt-1.5">{footerText}</p>
            )}
          </div>

          {/* Buttons */}
          {buttons && buttons.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {buttons.map((btn, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-[#d9fdd3] dark:bg-[#005c4b] px-3 py-1.5 text-xs font-medium text-[#00a884] dark:text-[#25d366]"
                >
                  <ButtonIcon type={btn.type} />
                  {btn.text}
                </div>
              ))}
            </div>
          )}

          {/* Bubble tail */}
          <div className="absolute -left-2 top-0 w-3 h-3 overflow-hidden">
            <div className="w-3 h-3 bg-[#d9fdd3] dark:bg-[#005c4b] rotate-45 transform origin-bottom-right" />
          </div>
        </div>
      </div>
    </button>
  );
}

// --- Variable Filling Dialog ---
export function VariableFillingDialog({
  open,
  onOpenChange,
  template,
  templateVariables,
  setTemplateVariable,
  workflowFields,
  lockTemplate = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: MessageTemplate;
  templateVariables: Record<string, string>;
  setTemplateVariable: (key: string, value: string) => void;
  /** When set, overrides recipient-based # autocomplete (workflow module fields) */
  workflowFields?: Array<{ value: string; label: string }>;
  /** When true, do not clear the template on close even if variables/media missing */
  lockTemplate?: boolean;
}) {
  const variables = template.variables || [];
  const [activeVar, setActiveVar] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const placeholderDropdownRef = useRef<HTMLDivElement>(null);

  // # placeholder autocomplete — reuses the same recipient-properties pattern as message-input
  const recipients = useMessageStore((s) => s.recipients);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [placeholderFilter, setPlaceholderFilter] = useState('');
  const [selectedPropIndex, setSelectedPropIndex] = useState(0);
  const [placeholderForVar, setPlaceholderForVar] = useState<string | null>(null);
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number; left: number } | null>(null);

  // Compute dropdown coords from the active variable input's bounding rect.
  // Using fixed positioning + a portal avoids clipping by the DialogContent's
  // overflow-hidden / scroll container.
  const updateDropdownCoords = useCallback((varName: string) => {
    const el = inputRefs.current[varName];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownCoords({ top: rect.bottom + 4, left: rect.left });
  }, []);

  // Recompute on scroll/resize while the dropdown is open
  useEffect(() => {
    if (!showPlaceholders || !placeholderForVar) return;
    const handler = () => updateDropdownCoords(placeholderForVar);
    handler();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [showPlaceholders, placeholderForVar, updateDropdownCoords]);

  // Compute available properties from workflowFields (if provided) or recipients (union of all property keys)
  const availableProperties = useMemo(() => {
    if (workflowFields && workflowFields.length > 0) {
      return workflowFields.map((f) => f.value);
    }
    const hasCrmOrCsv = recipients.some((r) => r.source === 'crm' || r.source === 'csv');
    if (!hasCrmOrCsv) return [];

    const keySet = new Set<string>();
    for (const r of recipients) {
      if (r.properties) {
        Object.keys(r.properties).forEach((k) => keySet.add(k));
      }
      keySet.add('name');
      keySet.add('phone');
      if (r.email) keySet.add('email');
    }
    return Array.from(keySet).sort();
  }, [recipients, workflowFields]);

  // Label map (workflow field label or raw property name)
  const propertyLabels = useMemo(() => {
    const m = new Map<string, string>();
    if (workflowFields) {
      for (const f of workflowFields) m.set(f.value, f.label);
    }
    return m;
  }, [workflowFields]);

  const filteredProperties = useMemo(() => {
    if (!placeholderFilter) return availableProperties;
    const q = placeholderFilter.toLowerCase();
    return availableProperties.filter((p) => p.toLowerCase().includes(q));
  }, [availableProperties, placeholderFilter]);

  // Handle "#" trigger in variable inputs
  const handleVariableChange = useCallback(
    (varName: string, value: string) => {
      setTemplateVariable(varName, value);

      // Detect "#" trigger for placeholder autocomplete
      if (availableProperties.length > 0) {
        const hashIndex = value.lastIndexOf('#');
        if (hashIndex >= 0) {
          const textAfterHash = value.substring(hashIndex + 1);
          if (!textAfterHash.includes(' ')) {
            setPlaceholderFilter(textAfterHash);
            setPlaceholderForVar(varName);
            setShowPlaceholders(true);
            setSelectedPropIndex(0);
            return;
          }
        }
      }
      setShowPlaceholders(false);
    },
    [setTemplateVariable, availableProperties]
  );

  // Insert selected property as {{property}} (normal) or ${MODULE.FIELD} (workflow) into the active variable input
  const insertProperty = useCallback(
    (property: string) => {
      if (!placeholderForVar) return;
      const currentVal = templateVariables[placeholderForVar] || '';
      const hashIndex = currentVal.lastIndexOf('#');
      const before = hashIndex >= 0 ? currentVal.substring(0, hashIndex) : currentVal;
      const placeholder = workflowFields && workflowFields.length > 0
        ? `\${${property}}`
        : `{{${property}}}`;
      const newValue = before + placeholder;
      setTemplateVariable(placeholderForVar, newValue);
      setShowPlaceholders(false);

      // Re-focus the input
      requestAnimationFrame(() => {
        inputRefs.current[placeholderForVar]?.focus();
      });
    },
    [placeholderForVar, templateVariables, setTemplateVariable, workflowFields]
  );

  // Scroll the selected item into view inside the dropdown
  useEffect(() => {
    if (!showPlaceholders || !placeholderDropdownRef.current) return;
    const container = placeholderDropdownRef.current;
    const selectedEl = container.querySelector(`[data-prop-index="${selectedPropIndex}"]`) as HTMLElement | null;
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedPropIndex, showPlaceholders]);

  // Keyboard navigation for placeholder dropdown
  const handleVariableKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showPlaceholders || filteredProperties.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedPropIndex((i) => Math.min(i + 1, filteredProperties.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedPropIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertProperty(filteredProperties[selectedPropIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowPlaceholders(false);
      }
    },
    [showPlaceholders, filteredProperties, selectedPropIndex, insertProperty]
  );

  // Close placeholder dropdown on outside click
  useEffect(() => {
    if (!showPlaceholders) return;
    const handler = (e: MouseEvent) => {
      if (placeholderDropdownRef.current && !placeholderDropdownRef.current.contains(e.target as Node)) {
        setShowPlaceholders(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPlaceholders]);

  // Media attachment from message store
  const attachedFile = useMessageStore((s) => s.attachedFile);
  const setAttachedFile = useMessageStore((s) => s.setAttachedFile);

  // Media templates always allow upload — template may have a sample URL but user still needs to provide their own
  const requiresMedia = !!template.headerMedia;
  const hasMedia = !!attachedFile;

  const filledCount = variables.filter((v) => templateVariables[v]?.trim()).length;
  const allVarsFilled = filledCount === variables.length;
  const allFilled = allVarsFilled && (!requiresMedia || hasMedia);

  // Parse body into text parts and variable parts
  const parts: ({ type: 'text'; value: string } | { type: 'var'; name: string })[] = [];
  const varPattern = /\{\{(\w+)\}\}/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  varPattern.lastIndex = 0;
  while ((match = varPattern.exec(template.body)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: template.body.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'var', name: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < template.body.length) {
    parts.push({ type: 'text', value: template.body.slice(lastIndex) });
  }

  // Focus the first unfilled variable on open
  useEffect(() => {
    if (open) {
      const firstUnfilled = variables.find((v) => !templateVariables[v]?.trim());
      if (firstUnfilled) {
        setActiveVar(firstUnfilled);
        requestAnimationFrame(() => {
          inputRefs.current[firstUnfilled]?.focus();
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-base">{template.name}</DialogTitle>
          <DialogDescription>
            Fill in the placeholder values directly in the message below.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-4 -mx-6 px-6">
          {/* Message preview with inline inputs — like the original SMS Ulgebra */}
          <div className="rounded-xl border border-green-200 dark:border-green-800/30 bg-green-50 dark:bg-[#1a2e23] px-4 py-4">
            {/* Header media — clickable upload for media templates */}
            {template.headerMedia && (
              <>
                {attachedFile ? (
                  /* Show uploaded file preview — same height as upload button */
                  <div className="relative rounded-lg overflow-hidden border border-green-300 dark:border-green-800 bg-white dark:bg-background mb-2 group cursor-pointer"
                    onClick={() => mediaInputRef.current?.click()}
                  >
                    {(attachedFile.previewUrl || attachedFile.remoteUrl) ? (
                      <img src={attachedFile.previewUrl || attachedFile.remoteUrl} alt="Uploaded" className="w-full h-20 object-cover" />
                    ) : null}
                    <div className="flex items-center gap-3 px-3 py-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30 shrink-0">
                        {template.headerMedia.type === 'image' && <Image className="h-5 w-5 text-green-600" />}
                        {template.headerMedia.type === 'video' && <Film className="h-5 w-5 text-green-600" />}
                        {template.headerMedia.type === 'document' && <FileUp className="h-5 w-5 text-green-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {attachedFile.file?.name || attachedFile.name || 'Saved media'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {attachedFile.file
                            ? `${(attachedFile.file.size / 1024).toFixed(1)} KB`
                            : attachedFile.remoteUrl
                              ? 'Remote file'
                              : ''}
                          <span className="mx-1.5">·</span>
                          Click to change
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setAttachedFile(null); }}
                        className="shrink-0 rounded-full p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Clickable upload placeholder */
                  <button
                    type="button"
                    onClick={() => mediaInputRef.current?.click()}
                    className={cn(
                      'w-full rounded-lg border-2 border-dashed flex items-center gap-3 px-3 py-3 mb-2 transition-colors cursor-pointer',
                      'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20',
                      'hover:border-primary hover:bg-primary/5'
                    )}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30 shrink-0">
                      {template.headerMedia.type === 'image' && <Image className="h-5 w-5 text-blue-500" />}
                      {template.headerMedia.type === 'video' && <Film className="h-5 w-5 text-blue-500" />}
                      {template.headerMedia.type === 'document' && <FileUp className="h-5 w-5 text-blue-500" />}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        Click to upload {template.headerMedia.type}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {template.headerMedia.type === 'image' && 'JPG, PNG, WebP'}
                        {template.headerMedia.type === 'video' && 'MP4, 3GP'}
                        {template.headerMedia.type === 'document' && 'PDF, DOC, DOCX, TXT'}
                      </p>
                    </div>
                  </button>
                )}
                {/* Hidden file input */}
                <input
                  ref={mediaInputRef}
                  type="file"
                  className="hidden"
                  accept={
                    template.headerMedia.type === 'image' ? 'image/*' :
                    template.headerMedia.type === 'video' ? 'video/*' :
                    '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx'
                  }
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
                    setAttachedFile({ file, previewUrl });
                    if (mediaInputRef.current) mediaInputRef.current.value = '';
                  }}
                />
              </>
            )}

            {/* Body text with inline input fields for each variable */}
            <div className="text-sm leading-[2.2] text-foreground dark:text-emerald-100 relative">
              {parts.map((part, i) =>
                part.type === 'text' ? (
                  <span key={i} className="whitespace-pre-wrap">{part.value}</span>
                ) : (
                  <span key={i} className="inline-flex items-center align-baseline mx-0.5 relative">
                    <input
                      ref={(el) => { inputRefs.current[part.name] = el; }}
                      value={templateVariables[part.name] || ''}
                      onChange={(e) => handleVariableChange(part.name, e.target.value)}
                      onFocus={() => {
                        setActiveVar(part.name);
                        setPlaceholderForVar(part.name);
                        updateDropdownCoords(part.name);
                      }}
                      onKeyDown={handleVariableKeyDown}
                      placeholder={availableProperties.length > 0 ? `# to insert` : `{{${part.name}}}`}
                      className={cn(
                        'inline-block rounded border bg-white dark:bg-background px-2 py-1 text-sm transition-all',
                        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                        'placeholder:text-muted-foreground/50 placeholder:text-xs',
                        'shadow-sm',
                        templateVariables[part.name]?.trim()
                          ? 'border-green-400 dark:border-green-700 text-foreground'
                          : 'border-blue-300 dark:border-blue-700 text-muted-foreground',
                        activeVar === part.name && 'ring-2 ring-primary ring-offset-1'
                      )}
                      style={{
                        width: `${Math.max(
                          (templateVariables[part.name] || '').length,
                          part.name.length + 4
                        ) * 0.6 + 1.5}em`,
                        minWidth: '5em',
                        maxWidth: '100%',
                      }}
                    />
                  </span>
                )
              )}
            </div>

            {/* # hint */}
            {availableProperties.length > 0 && variables.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
                <Hash className="h-3 w-3" />
                Type <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">#</kbd> in a field to insert recipient data
              </p>
            )}

            {/* Footer */}
            {template.footerText && (
              <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-green-200 dark:border-green-900/30">
                {template.footerText}
              </p>
            )}

            {/* Buttons — show URL with inline inputs when URL has placeholders */}
            {template.buttons && template.buttons.length > 0 && (
              <div className="mt-3 pt-2 border-t border-green-200 dark:border-green-900/30 space-y-2">
                {template.buttons.map((btn, btnIdx) => {
                  // Check if this button's URL contains {{placeholders}}
                  const hasUrlPlaceholders = btn.url && /\{\{\w+\}\}/.test(btn.url);

                  return (
                    <div key={btnIdx}>
                      {/* Button label */}
                      <div className="flex items-center justify-center gap-1.5 rounded-lg bg-white/60 dark:bg-background/40 px-3 py-1.5 text-xs font-medium text-primary">
                        <ButtonIcon type={btn.type} />
                        {btn.text}
                      </div>

                      {/* Button URL with inline input for placeholders */}
                      {hasUrlPlaceholders && btn.url && (
                        <div className="mt-1 text-xs leading-[2.2] text-muted-foreground break-all px-1">
                          {(() => {
                            // Parse button URL into text + variable parts
                            const urlParts: ({ type: 'text'; value: string } | { type: 'var'; name: string; varKey: string })[] = [];
                            const urlVarPattern = /\{\{(\w+)\}\}/g;
                            let urlMatch: RegExpExecArray | null;
                            let urlLastIndex = 0;
                            urlVarPattern.lastIndex = 0;
                            while ((urlMatch = urlVarPattern.exec(btn.url!)) !== null) {
                              if (urlMatch.index > urlLastIndex) {
                                urlParts.push({ type: 'text', value: btn.url!.slice(urlLastIndex, urlMatch.index) });
                              }
                              // The variable key uses URL_ prefix as created in twilio module
                              const rawName = urlMatch[1];
                              const varKey = variables.includes(rawName) ? rawName : `URL_${rawName}`;
                              urlParts.push({ type: 'var', name: rawName, varKey });
                              urlLastIndex = urlMatch.index + urlMatch[0].length;
                            }
                            if (urlLastIndex < btn.url!.length) {
                              urlParts.push({ type: 'text', value: btn.url!.slice(urlLastIndex) });
                            }

                            return (
                              <>
                                <span className="text-muted-foreground/60">[</span>
                                {urlParts.map((urlPart, ui) =>
                                  urlPart.type === 'text' ? (
                                    <span key={ui}>{urlPart.value}</span>
                                  ) : (
                                    <span key={ui} className="inline-flex items-center align-baseline mx-0.5">
                                      <input
                                        ref={(el) => { inputRefs.current[urlPart.varKey] = el; }}
                                        value={templateVariables[urlPart.varKey] || ''}
                                        onChange={(e) => setTemplateVariable(urlPart.varKey, e.target.value)}
                                        onFocus={() => setActiveVar(urlPart.varKey)}
                                        placeholder={`{{${urlPart.name}}}`}
                                        className={cn(
                                          'inline-block rounded border bg-white dark:bg-background px-2 py-0.5 text-xs transition-all',
                                          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                                          'placeholder:text-muted-foreground/50',
                                          'shadow-sm',
                                          templateVariables[urlPart.varKey]?.trim()
                                            ? 'border-green-400 dark:border-green-700 text-foreground'
                                            : 'border-blue-300 dark:border-blue-700 text-muted-foreground',
                                          activeVar === urlPart.varKey && 'ring-2 ring-primary ring-offset-1'
                                        )}
                                        style={{
                                          width: `${Math.max(
                                            (templateVariables[urlPart.varKey] || '').length,
                                            urlPart.name.length + 4
                                          ) * 0.6 + 1.5}em`,
                                          minWidth: '5em',
                                          maxWidth: '100%',
                                        }}
                                      />
                                    </span>
                                  )
                                )}
                                <span className="text-muted-foreground/60">]</span>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Progress + Done button — pinned at bottom */}
        <DialogFooter className="flex-row items-center justify-between gap-2 shrink-0 border-t pt-4">
          <div className="flex flex-col gap-0.5">
            {variables.length > 0 && (
              <span className={cn('text-xs flex items-center gap-1', allVarsFilled ? 'text-green-600' : 'text-amber-600')}>
                {allVarsFilled ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {allVarsFilled ? 'All variables filled' : `${filledCount}/${variables.length} variables filled`}
              </span>
            )}
            {requiresMedia && (
              <span className={cn('text-xs flex items-center gap-1', hasMedia ? 'text-green-600' : 'text-amber-600')}>
                {hasMedia ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {hasMedia ? 'Media attached' : `${template.headerMedia!.type} required`}
              </span>
            )}
          </div>
          <Button size="sm" onClick={() => onOpenChange(false)} disabled={!allFilled}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* # Placeholder autocomplete dropdown — rendered via portal with fixed
          positioning so it escapes the DialogContent's overflow-hidden clipping. */}
      {showPlaceholders && dropdownCoords && filteredProperties.length > 0 && createPortal(
        <div
          ref={placeholderDropdownRef}
          style={{
            position: 'fixed',
            top: dropdownCoords.top,
            left: dropdownCoords.left,
          }}
          className="w-96 max-h-48 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl z-[200] backdrop-blur-sm"
        >
          <div className="px-3.5 py-2 border-b border-border bg-muted/30">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {workflowFields && workflowFields.length > 0 ? 'Module Fields' : 'Recipient Fields'}
            </p>
          </div>
          {filteredProperties.map((prop, idx) => {
            const isWorkflow = !!(workflowFields && workflowFields.length > 0);
            const label = propertyLabels.get(prop) || prop;
            const hint = isWorkflow ? `\${${prop}}` : `{{${prop}}}`;
            return (
              <button
                key={prop}
                type="button"
                data-prop-index={idx}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertProperty(prop);
                }}
                className={cn(
                  'w-full text-left px-3.5 py-2.5 text-sm transition-colors flex items-center gap-2.5',
                  idx === selectedPropIndex
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-muted text-foreground'
                )}
              >
                <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 font-medium">{label}</span>
                <span className="text-[10px] text-muted-foreground font-mono shrink-0 bg-muted px-1.5 py-0.5 rounded">
                  {hint}
                </span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </Dialog>
  );
}

// --- Custom template ID prefix ---
const CUSTOM_TEMPLATE_PREFIX = 'ua_msg_template_item_';

// --- Template Dialog ---
export function TemplateDialog({
  open,
  onOpenChange,
  templates,
  loading,
  selected,
  onSelect,
  channel,
  onSaveTemplate,
  onDeleteTemplate,
  hideCustomActions,
  forceRefresh,
  refreshing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: MessageTemplate[];
  loading: boolean;
  selected: MessageTemplate | null;
  onSelect: (t: MessageTemplate) => void;
  channel: string;
  onSaveTemplate?: (name: string, message: string, visibility?: 'all_users' | 'only_me', saasModule?: string) => Promise<void>;
  onDeleteTemplate?: (templateId: string) => Promise<void>;
  /** When true, hides "+ New Template" button and external template links (e.g. 24h window expired context) */
  hideCustomActions?: boolean;
  /** Force-refresh templates bypassing proxy cache */
  forceRefresh?: () => Promise<void>;
  /** Whether a refresh is currently in progress */
  refreshing?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showManageMenu, setShowManageMenu] = useState(false);
  const manageMenuRef = useRef<HTMLDivElement>(null);
  // Determine extension type for external template links
  const extensionName = useAppStore((s) => s.extensionName) ?? '';
  const isTwilio = extensionName.startsWith('twilio');
  const isWhatsApp = extensionName.startsWith('whatsapp');
  const wabaIds = useWhatsAppStore((s) => s.availableWabaIds);

  // Current sender's WABA ID — used to filter templates when a specific sender is selected
  const currentSender = useMessageStore((s) => s.sender);
  const senderWabaId = currentSender?.data?.wabaId as string | undefined;

  // Close manage menu on outside click
  useEffect(() => {
    if (!showManageMenu) return;
    const handler = (e: MouseEvent) => {
      if (manageMenuRef.current && !manageMenuRef.current.contains(e.target as Node)) {
        setShowManageMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showManageMenu]);

  // Pre-filter templates by sender's WABA (only when a specific sender with wabaId is selected)
  const wabaFilteredTemplates = useMemo(() => {
    if (!senderWabaId) return templates;
    return templates.filter((t) => !t.wabaId || t.wabaId === senderWabaId);
  }, [templates, senderWabaId]);

  // Recompute labels when dialog opens so localStorage usage data is fresh
  const labels = useMemo(() => getTemplateLabels(wabaFilteredTemplates), [wabaFilteredTemplates, open]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    wabaFilteredTemplates.forEach((t) => {
      if (t.category) cats.add(t.category);
    });
    return Array.from(cats).sort();
  }, [wabaFilteredTemplates]);

  const filtered = useMemo(() => {
    let result = wabaFilteredTemplates;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.body.toLowerCase().includes(q)
      );
    }
    if (categoryFilter) {
      result = result.filter((t) => t.category === categoryFilter);
    }
    return sortTemplates(result, labels);
  }, [wabaFilteredTemplates, search, categoryFilter, labels]);

  const handleDeleteTemplate = async (templateId: string) => {
    if (!onDeleteTemplate) return;
    setDeletingId(templateId);
    try {
      await onDeleteTemplate(templateId);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="template-picker-dialog" className="max-w-[95vw] sm:max-w-2xl md:max-w-4xl lg:max-w-6xl flex flex-col overflow-hidden py-3 px-2 sm:py-4 sm:px-6" style={{ maxHeight: '95vh' }}>
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 sm:gap-4 pl-2">
            <DialogTitle className="text-base sm:text-lg shrink-0">Templates</DialogTitle>
            <DialogDescription className="sr-only">
              Select a message template for {channel}
            </DialogDescription>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0 mr-8">
              {forceRefresh && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0 h-8 w-8 p-0"
                  disabled={refreshing}
                  onClick={() => forceRefresh()}
                  title="Refresh templates"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
                </Button>
              )}
              {isTwilio && (
                <div className="relative" ref={manageMenuRef}>
                  <div className="flex items-center">
                    <a
                      href={channel === 'WHATSAPP'
                        ? 'https://console.twilio.com/us1/develop/sms/senders/whatsapp-templates'
                        : 'https://console.twilio.com/us1/develop/sms/content-template-builder'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="sm" variant="outline" className="text-xs rounded-r-none border-r-0">
                        <Settings className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Manage Templates</span>
                        <span className="sm:hidden">Manage</span>
                      </Button>
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs rounded-l-none px-1.5"
                      onClick={() => setShowManageMenu(!showManageMenu)}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {showManageMenu && (
                    <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-md border border-border bg-popover shadow-md py-1">
                      <button
                        onClick={() => {
                          setShowManageMenu(false);
                          onOpenChange(false);
                          setNewTemplateOpen(true);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Create Local Template
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isWhatsApp && wabaIds.length > 0 && (
                <div className="relative" ref={!isTwilio ? manageMenuRef : undefined}>
                  <div className="flex items-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className={cn("text-xs", !hideCustomActions && "rounded-r-none border-r-0")}
                      onClick={() => {
                        onOpenChange(false);
                        useUIStore.getState().setActiveSettingsTab('template-manager');
                        useUIStore.getState().setView('SETTINGS');
                      }}
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Manage Templates
                    </Button>
                    {!hideCustomActions && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs rounded-l-none px-1.5"
                        onClick={() => setShowManageMenu(!showManageMenu)}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  {!hideCustomActions && showManageMenu && (
                    <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-md border border-border bg-popover shadow-md py-1">
                      <button
                        onClick={() => {
                          setShowManageMenu(false);
                          onOpenChange(false);
                          setNewTemplateOpen(true);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Create Local Template
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!hideCustomActions && !isTwilio && !isWhatsApp && onSaveTemplate && (
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setNewTemplateOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  New Template
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Search + Category row — stays pinned */}
        <div className="flex items-center gap-3 flex-wrap mt-3 shrink-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setCategoryFilter(null)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  !categoryFilter
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat === categoryFilter ? null : cat)}
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                    categoryFilter === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Template grid — scrolls */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-2 -mx-2 px-2 sm:-mx-6 sm:px-6 pt-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading templates...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">
                {search ? 'No templates match your search' : 'No templates available'}
              </p>
              {/* Empty-state CTA — same trigger as the header dropdown's
                  "Create Local Template" entry. Only renders when the
                  consumer wired up `onSaveTemplate` and didn't suppress
                  custom-template actions (hideCustomActions). Without
                  this, a user with zero templates had no visible path
                  to create one — the dropdown that exposes the option
                  is hidden until templates exist. */}
              {!search && !hideCustomActions && onSaveTemplate && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-4"
                  onClick={() => setNewTemplateOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Create Local Template
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 pb-4">
              {filtered.map((t) => {
                const isCustom = t.id.startsWith(CUSTOM_TEMPLATE_PREFIX);
                return (
                  <div key={t.id} className="relative group/card">
                    <PhonePreview
                      template={t}
                      name={t.name}
                      label={labels.get(t.id) ?? null}
                      isSelected={selected?.id === t.id}
                      isCustom={isCustom}
                      onClick={() => onSelect(t)}
                    />
                    {isCustom && !hideCustomActions && (
                      <div className="absolute top-1.5 right-1.5 z-10 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-all">
                        {onSaveTemplate && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTemplate(t);
                            }}
                            className="rounded-full p-1.5 bg-background/80 backdrop-blur border border-border shadow-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                            title="Edit template"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {onDeleteTemplate && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(t.id);
                            }}
                            disabled={deletingId === t.id}
                            className="rounded-full p-1.5 bg-background/80 backdrop-blur border border-border shadow-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                            title="Delete template"
                          >
                            {deletingId === t.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </DialogContent>
    </Dialog>

    {/* New Template Editor Dialog — rendered outside parent Dialog to avoid nested dialog issues */}
    {!hideCustomActions && onSaveTemplate && (
      <TemplateEditorDialog
        open={newTemplateOpen}
        onOpenChange={setNewTemplateOpen}
        template={null}
        channel={channel as import('@/types/messaging').Channel}
        onSave={async (tmpl) => {
          await onSaveTemplate(tmpl.name, tmpl.body, 'all_users', (tmpl as Record<string, unknown>).saasModule as string | undefined);
        }}
      />
    )}

    {/* Edit Template Dialog */}
    {!hideCustomActions && onSaveTemplate && editingTemplate && (
      <TemplateEditorDialog
        open={!!editingTemplate}
        onOpenChange={(open) => { if (!open) setEditingTemplate(null); }}
        template={editingTemplate}
        channel={channel as import('@/types/messaging').Channel}
        onSave={async (tmpl) => {
          await onSaveTemplate(tmpl.name, tmpl.body, 'all_users', (tmpl as Record<string, unknown>).saasModule as string | undefined);
          setEditingTemplate(null);
        }}
      />
    )}
    </>
  );
}
